// Sustained glyph rain (sanctions INVOICE columns): emitters with a reserved slot range per registry entry, each
// spawning particle columns over one anchor for the placement's duration through the ParticleSystem.

import type { Color } from 'three'
import { InvariantViolation, NEUTRAL_OWNER } from '@taxwar/net'
import { effectsOf, type EffectId, type RainDef } from './effects'
import type { ParticleSystem } from './ParticleSystem'
import { createPointPlacement, type PointPlacement } from './placement'
import { SlotRange, scaledCount } from './slots'

export class RainSystem {
  private readonly ranges = new Map<EffectId, SlotRange>()
  private readonly defs: (RainDef | null)[]
  private readonly anchors: readonly PointPlacement[]
  /** Live palette colours, kept by reference: columns spawned after a colour-blind toggle take the new hue. */
  private readonly colours: (Color | null)[]
  private readonly ends: Float64Array
  /** Fractional columns owed to each emitter. */
  private readonly owed: Float32Array

  constructor(
    private readonly particles: ParticleSystem,
    private readonly scale: number,
  ) {
    let capacity = 0
    for (const [id, def] of effectsOf('rain')) {
      const count = scaledCount(def.maxConcurrent, scale)
      this.ranges.set(id, new SlotRange(capacity, count))
      capacity += count
    }
    this.defs = new Array<RainDef | null>(capacity).fill(null)
    this.anchors = Array.from({ length: capacity }, () => createPointPlacement())
    this.colours = new Array<Color | null>(capacity).fill(null)
    this.ends = new Float64Array(capacity)
    this.owed = new Float32Array(capacity)
  }

  /** `colour` is a live palette colour: kept by reference, never mutated. */
  play(id: EffectId, def: RainDef, placement: PointPlacement, colour: Color, now: number): void {
    const range = this.ranges.get(id)
    if (range === undefined) throw new InvariantViolation(`rain effect ${id} has no reserved slots`)
    if (!(placement.seconds > 0)) throw new InvariantViolation(`rain effect ${id} played for ${placement.seconds} s`)
    const slot = range.claim()
    const anchor = this.anchors[slot] as PointPlacement
    anchor.owner = NEUTRAL_OWNER
    anchor.at.copy(placement.at)
    anchor.altitude = placement.altitude
    anchor.extent = placement.extent
    anchor.amount = 0
    anchor.seconds = placement.seconds
    this.colours[slot] = colour
    this.defs[slot] = def
    this.ends[slot] = now + placement.seconds
    // The first column falls at once.
    this.owed[slot] = 1
  }

  update(now: number, dtSeconds: number): void {
    for (let slot = 0; slot < this.defs.length; slot++) {
      const def = this.defs[slot]
      if (def === null || def === undefined) continue
      if (now >= (this.ends[slot] as number)) {
        this.defs[slot] = null
        this.colours[slot] = null
        continue
      }
      let owed = (this.owed[slot] as number) + dtSeconds * def.columnsPerSecond * this.scale
      while (owed >= 1) {
        owed -= 1
        // Out of this frame's particle budget: the column is dropped, not queued.
        if (!this.particles.spawnUnit(def.particle, this.anchors[slot] as PointPlacement, this.colours[slot] as Color)) owed = 0
      }
      this.owed[slot] = owed
    }
  }
}
